import type { Table as TanStackTable } from "@tanstack/react-table";

/**
 * Extracted cell components for the AssignWritersStep bulk assignment table.
 *
 * Each component subscribes to its own slice of the Zustand assignment store
 * via a granular selector, so only the affected cell re-renders when a single
 * assignment toggles — rather than the entire table re-rendering via a new
 * `columns` reference.
 */
import { FormCheckbox, IconCross, IconLock, IconTick, IconUnlock, Text } from "@sentricmusic/ui";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { useUIStateStore } from "@/store/uiStateStore";

import type { SelectedWriter } from "../types/writer";

import useSongWriterAssignmentStore from "../store/songWriterAssignmentStore";

// ────────────────────────────────────────────────────────────
// Shared row type (must match SongWriterRow in AssignWritersStep)
// ────────────────────────────────────────────────────────────
export type SongWriterRow = {
  songId: string;
  songTitle: string;
  artistName: string;
  duration: string;
  isRecordingOrVariant?: boolean;
  recordingLabel?: string;
  [key: string]: unknown;
};

// ────────────────────────────────────────────────────────────
// AssignmentCell – one tick/cross per song × writer intersection
// Re-renders only when its own boolean flips.
// ────────────────────────────────────────────────────────────
type AssignmentCellProps = {
  songId: string;
  songTitle: string;
  writerCode: string;
  writerName: string;
  isLocked: boolean;
  onToggle: (songId: string, writerCode: string) => void;
};

export const AssignmentCell = memo(({
  songId,
  songTitle,
  writerCode,
  writerName,
  isLocked,
  onToggle,
}: AssignmentCellProps) => {
  const { t } = useTranslation("album");
  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Granular selector — only re-renders when THIS specific boolean changes
  const isAssigned = useSongWriterAssignmentStore(
    state => state.assignments[songId]?.[writerCode] || false,
  );

  return (
    <div className="flex justify-center">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!isLocked) {
            onToggle(songId, writerCode);
          }
        }}
        disabled={isLocked && !isAssigned}
        title={
          isLocked && isAssigned
            ? "This writer is locked to this song"
            : isLocked
              ? "This writer is not assigned to this song"
              : isAssigned
                ? `${t("album:remove", "Remove")} ${writerName} ${t("album:from", "from")} ${songTitle}`
                : `${t("album:assign", "Assign")} ${writerName} ${t("album:to", "to")} ${songTitle}`
        }
        className={`w-full h-6 sm:h-8 rounded-md transition-colors duration-200 font-medium text-lg sm:text-2xl flex items-center justify-center ${
          isLocked && !isAssigned
            ? "bg-layer-raised/50 text-ink-secondary cursor-not-allowed opacity-60 border border-canvas-inverse/25"
            : isAssigned
              ? isTunecore
                ? "bg-action-secondary text-black hover:bg-action-secondary/50"
                : "bg-action-secondary text-white hover:bg-action-secondary/50"
              : "border border-canvas-inverse/25  hover:bg-layer-raised/50 text-black"
        }`}
      >
        {isAssigned ? <IconTick className="w-4 h-4 sm:w-6 sm:h-6 stroke-white" /> : <IconCross className="w-4 h-4 sm:w-6 sm:h-6 stroke-ink-primary" />}
      </button>
    </div>
  );
});

// ────────────────────────────────────────────────────────────
// AssignmentHeaderCell – the writer column header with "select all" checkbox
// Re-renders only when any assignment for this writer column changes.
// ────────────────────────────────────────────────────────────
type AssignmentHeaderCellProps = {
  writer: SelectedWriter;
  editableSongIds: string[];
  isAuxWriter: boolean;
  isUnlocked: boolean;
  onToggleAll: (writerCode: string, checked: boolean) => void;
  onToggleLock: (writerCode: string) => void;
};

export const AssignmentHeaderCell = memo(({
  writer,
  editableSongIds,
  isAuxWriter,
  isUnlocked,
  onToggleAll,
  onToggleLock,
}: AssignmentHeaderCellProps) => {
  const { t } = useTranslation("album");
  const isTunecore = useUIStateStore(state => state.isTunecore);

  // Granular selector — only re-renders when any editable song's assignment
  // for THIS writer changes. useShallow does shallow array comparison.
  const assignmentValues = useSongWriterAssignmentStore(
    useShallow(state =>
      editableSongIds.map(songId => state.assignments[songId]?.[writer.writercode] || false),
    ),
  );

  const isAllChecked = editableSongIds.length > 0
    && assignmentValues.every(Boolean);

  const displayName = writer.writername;
  const ipiNumber = writer.caeipinumber ?? "No IPI";

  return (
    <div className="flex flex-col items-center justify-center w-full h-full gap-1 p-1 overflow-hidden sm:p-2">
      {isAuxWriter && (
        <div className="flex items-center justify-start w-full gap-2">
          <button
            type="button"
            onClick={() => onToggleLock(writer.writercode)}
            className={`flex flex-col items-center p-2 transition-colors rounded-lg ${isTunecore ? "bg-layer-inverse" : "bg-canvas-inverse/20"}`}
            title={isUnlocked ? "Lock this writer to their assigned songs" : "Unlock to assign to other songs"}
          >
            {isUnlocked ? (
              <IconUnlock className={`w-5 h-5 ${isTunecore ? "stroke-white" : "stroke-canvas-inverse"}`} />
            ) : (
              <IconLock className={`w-5 h-5 ${isTunecore ? "stroke-white" : "stroke-canvas-inverse"}`} />
            )}
          </button>
          <div className="min-w-0 overflow-hidden">
            <Text as="bodySm" className={`block text-xs font-medium leading-tight uppercase truncate sm:text-sm ${isTunecore ? "text-white" : "text-ink-primary"}`}>
              {displayName}
            </Text>
            <Text as="bodySm" className={`hidden text-xs leading-tight truncate sm:block ${isTunecore ? "text-white/70" : "text-ink-primary/70"}`}>
              {ipiNumber}
            </Text>
          </div>
        </div>
      )}
      {!isAuxWriter && (
        <button
          type="button"
          className={`flex items-center justify-center w-full h-full transition-colors rounded ${isTunecore ? "hover:bg-white/10" : "hover:bg-canvas-inverse/10"}`}
          onClick={() => onToggleAll(writer.writercode, !isAllChecked)}
          // I know how much the below code sucks to look at, please dont @ me - Matt
          title={`${isAllChecked ? (t("album:remove") ?? "Remove") : (t("album:assign") ?? "Assign")} ${displayName} ${isAllChecked ? (t("album:from") ?? "from") : (t("album:to") ?? "to")} ${t("album:all-songs") ?? "all songs"}`}
          aria-label={`${isAllChecked ? (t("album:remove") ?? "Remove") : (t("album:assign") ?? "Assign")} ${displayName} ${isAllChecked ? (t("album:from") ?? "from") : (t("album:to") ?? "to")} ${t("album:all-songs") ?? "all songs"}`}
        >
          <FormCheckbox
            tableHeader={true}
            checked={isAllChecked}
            onChange={() => {}}
            className="shrink-0 w-3 h-3 mr-1 pointer-events-none sm:w-4 sm:h-4 sm:mr-2"
          />
          <div className="flex-1 min-w-0 overflow-hidden text-left">
            <Text as="bodySm" className={`block text-xs font-medium leading-tight uppercase truncate sm:text-sm ${isTunecore ? "text-white" : "text-ink-primary"}`}>
              {displayName}
            </Text>
            <Text as="bodySm" className={`hidden text-xs leading-tight truncate sm:block ${isTunecore ? "text-white/70" : "text-ink-primary/70"}`}>
              {ipiNumber}
            </Text>
          </div>
        </button>
      )}
    </div>
  );
});

// ────────────────────────────────────────────────────────────
// SongTitleCell – the pinned song title column with "assign all writers" checkbox
// Re-renders only when any assignment for THIS song changes.
// ────────────────────────────────────────────────────────────
type SongTitleCellProps = {
  songId: string;
  songTitle: string;
  unlockedWriterCodes: string[];
  onAssignAll: (songId: string, checked: boolean) => void;
};

export const SongTitleCell = memo(({
  songId,
  songTitle,
  unlockedWriterCodes,
  onAssignAll,
}: SongTitleCellProps) => {
  // Granular selector — only re-renders when this song's assignments change
  const assignmentValues = useSongWriterAssignmentStore(
    useShallow(state =>
      unlockedWriterCodes.map(code => state.assignments[songId]?.[code] || false),
    ),
  );

  const allUnlockedWritersAssigned = unlockedWriterCodes.length > 0
    && assignmentValues.every(Boolean);

  return (
    <div className="flex items-center flex-1 gap-2 overflow-hidden">
      <Text as="p" className="flex-1 min-w-0 text-sm font-semibold truncate">
        {songTitle}
      </Text>
      <FormCheckbox
        id={`checkbox-all-writers-${songId}`}
        checked={Boolean(allUnlockedWritersAssigned)}
        onChange={e => onAssignAll(songId, e.target.checked)}
        className="w-5 h-5 cursor-pointer shrink-0"
      />
    </div>
  );
});

// ────────────────────────────────────────────────────────────
// Helper: extract editable song IDs from a TanStack table instance
// Used by AssignmentHeaderCell to know which songs to check
// ────────────────────────────────────────────────────────────
export function getEditableSongIds(table: TanStackTable<SongWriterRow>): string[] {
  return table
    .getRowModel()
    .rows
    .filter(row => !row.original.isRecordingOrVariant)
    .map(row => row.original.songId);
}
